import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ConfigService,
  ServiceWithConfigBase,
} from './config.service';
import { MaintenanceStatus } from './models/api.model';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService extends ServiceWithConfigBase {
  private maintenanceOnSubject = new BehaviorSubject<boolean>(false);
  public maintenanceOn$ = this.maintenanceOnSubject.asObservable();

  constructor(configService: ConfigService) {
    super(configService);
  }

  checkMaintenanceStatus(): Observable<boolean> {
    return this.wrapForConfig((config) => {
      if (
        !config.featureFlags ||
        !config.featureFlags.maintenanceCheck
      ) {
        this.maintenanceOnSubject.next(false);
        return of(false);
      }

      if (!environment.production) {
        const mockResponse: MaintenanceStatus = {
          maintenanceEnabled: true,
        };

        const isUnderMaintenance = mockResponse.maintenanceEnabled;
        this.maintenanceOnSubject.next(isUnderMaintenance);
        return of(isUnderMaintenance);
      }

      return new Observable<boolean>((observer) => {
        let maintenanceStatus = false;

        fetch(`${config.apiUrl}/maintenance/status`)
          .then((response) => {
            // Service unavailable - system is under maintenance
            if (response.status === 503) {
              return true;
            }

            if (response.status === 200) {
              return response
                .json()
                .then(
                  (body: MaintenanceStatus) =>
                    body?.maintenanceEnabled || false,
                )
                .catch((error) => {
                  console.warn(
                    'Failed to parse maintenance response:',
                    error,
                  );
                  return false;
                });
            }

            console.warn(
              `Unexpected status code: ${response.status}`,
            );
            return false;
          })
          .then((isUnderMaintenance) => {
            maintenanceStatus = isUnderMaintenance;
            observer.next(maintenanceStatus);
            observer.complete();
          })
          .catch((error) => {
            console.error(
              'Error checking maintenance status:',
              error,
            );
            observer.next(false);
            observer.complete();
          })
          .finally(() => {
            this.maintenanceOnSubject.next(maintenanceStatus);
          });
      }).pipe(
        catchError((error) => {
          console.error(
            'Unexpected error in maintenance check:',
            error,
          );
          this.maintenanceOnSubject.next(false);
          return of(false);
        }),
      );
    });
  }
}
